import React from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Play, Heart, Star, ShieldCheck, Film } from "lucide-react";
import { CopyShareLink } from "@/components/CopyShareLink";

export function Spinner() {
  return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div>;
}

export function PageHeader({ title, subtitle, icon: Icon, action }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold flex items-center gap-3">
          {Icon && <Icon className="w-8 h-8 text-[hsl(var(--primary))]" />} {title}
        </h1>
        {subtitle && <p className="text-[hsl(var(--muted-foreground))] mt-1.5">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function StatCard({ label, value, icon: Icon, accent = "#3b9eff", delay = 0 }) {
  return (
    <div className={`rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 card-hover animate-rise delay-${delay}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-[hsl(var(--muted-foreground))] font-medium">{label}</span>
        {Icon && <div className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: `${accent}22` }}><Icon className="w-5 h-5" style={{ color: accent }} /></div>}
      </div>
      <div className="font-display text-3xl font-extrabold">{value}</div>
    </div>
  );
}

export function EmptyState({ emoji = "🎮", title, text, action }) {
  return (
    <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-12 text-center">
      <div className="text-5xl mb-3">{emoji}</div>
      <h3 className="font-display text-xl font-bold mb-1">{title}</h3>
      {text && <p className="text-[hsl(var(--muted-foreground))] mb-4 max-w-sm mx-auto">{text}</p>}
      {action}
    </div>
  );
}

export function GameCard({ game, onClick, badge, shareId, noShare = false }) {
  const w = game.world || {};
  const sid = shareId || game.id;
  const isNew = (() => {
    const t = game.published_at;
    if (!t) return false;
    const d = new Date(t);
    return !isNaN(d) && (Date.now() - d.getTime()) < 7 * 864e5;
  })();
  const card = (
    <button onClick={onClick} data-testid={`game-card-${game.id}`}
      className="text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden card-hover group w-full">
      <div className="relative h-32 grid place-items-center overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${w.bgColor || "#0b1026"}, ${w.accent || "#1f6feb"})` }}>
        {game.cover_image ? (
          <img src={`${process.env.REACT_APP_BACKEND_URL}${game.cover_image}`} alt={game.title}
            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
        ) : (
          <span className="text-5xl group-hover:scale-110 transition-transform">{game.cover_emoji || "🎮"}</span>
        )}
        {badge && <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/50 text-white backdrop-blur">{badge}</span>}
        {isNew && (
          <span title="Published in the last 7 days" data-testid={`new-badge-${game.id}`}
            className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-500 text-white shadow-lg shadow-emerald-500/30">
            NEW
          </span>
        )}
        {game.licence?.verified && (
          <span title="Verified original — no protected IP detected" data-testid={`verified-badge-${game.id}`}
            className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-black/55 text-emerald-300 backdrop-blur">
            <ShieldCheck className="w-3 h-3" /> Verified
          </span>
        )}
        {game.hot_clips && (
          <span title="Hot player clips — trending gameplay" data-testid={`hot-clips-${game.id}`}
            className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-fuchsia-500/80 text-white backdrop-blur">
            <Film className="w-3 h-3" /> Hot clips
          </span>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors grid place-items-center">
          <Play className="w-10 h-10 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="white" />
        </div>
      </div>
      <div className="p-3.5">
        <div className="font-bold truncate">{game.title}</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">by {game.owner_name}{game.owner_verified && <ShieldCheck className="inline w-3 h-3 text-emerald-500 ml-1 -mt-0.5" aria-label="Verified Creator" />}</div>
        <div className="flex items-center gap-3 mt-2 text-xs text-[hsl(var(--muted-foreground))]">
          <span className="flex items-center gap-1"><Play className="w-3 h-3" /> {game.plays || 0}</span>
          <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {game.likes || 0}</span>
          {game.rating > 0 && <span className="flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {game.rating}</span>}
        </div>
      </div>
    </button>
  );
  if (noShare || !sid) return card;
  return (
    <div className="relative group/card">
      {card}
      <div className="absolute top-2 right-2 opacity-0 group-hover/card:opacity-100 transition-opacity">
        <CopyShareLink gameId={sid} />
      </div>
    </div>
  );
}
